import React, { useEffect, useState, useCallback } from "react";
import { Routes, Route, Link, useParams, useNavigate, NavLink } from "react-router-dom";
import { api, API_BASE, getToken, formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { PaginationBar } from "@/components/PaginationBar";
import { WidgetError } from "@/components/WidgetError";
import { fmtMoney, fmtDate } from "@/lib/format";
import { toast } from "sonner";
import { Receipt, CircleDollarSign, FileText, ChevronRight, CheckCircle2, XCircle } from "lucide-react";

/**
 * Stage 3A — Platform Owner SaaS Billing module.
 * Fully separate from the tenant→customer finance module.
 */
export default function SaasBillingModule() {
  return (
    <div className="min-w-0">
      <div className="mb-6 flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="label-overline">Platform</div>
          <h1 className="text-3xl font-black tracking-tight mt-1">SaaS Billing</h1>
          <p className="text-slate-600 mt-1 text-sm">
            Invoicing and payment collection for TruckStop SaaS platform usage.
          </p>
        </div>
      </div>

      <div className="flex gap-2 border-b border-slate-200 mb-4 overflow-x-auto">
        {[
          { to: "/platform/saas/invoices", label: "Invoices", icon: FileText, testId: "saas-tab-invoices" },
          { to: "/platform/saas/queue", label: "Manual EFT queue", icon: Receipt, testId: "saas-tab-queue" },
          { to: "/platform/saas/payments", label: "Payments", icon: CircleDollarSign, testId: "saas-tab-payments" },
        ].map((t) => (
          <NavLink key={t.to} to={t.to} end
                   data-testid={t.testId}
                   className={({ isActive }) =>
                     "flex items-center gap-2 px-3 py-2 text-sm font-semibold whitespace-nowrap border-b-2 " +
                     (isActive ? "border-blue-700 text-blue-700" : "border-transparent text-slate-500 hover:text-slate-800")}>
            <t.icon size={14} /> {t.label}
          </NavLink>
        ))}
      </div>

      <Routes>
        <Route index element={<SaasInvoices />} />
        <Route path="invoices" element={<SaasInvoices />} />
        <Route path="invoices/:id" element={<SaasInvoiceDetail />} />
        <Route path="invoices/new" element={<SaasInvoiceCreate />} />
        <Route path="queue" element={<SaasPaymentsQueue />} />
        <Route path="payments" element={<SaasPayments />} />
      </Routes>
    </div>
  );
}

// ---------------- Invoices ----------------

function SaasInvoices() {
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [status, setStatus] = useState("");
  const [q, setQ] = useState("");
  const [error, setError] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => { setPage(1); }, [status, q]);

  useEffect(() => {
    let c = false;
    setError(null);
    const params = { page, limit: 20 };
    if (status) params.status = status;
    if (q) params.q = q;
    api.get("/platform/saas/invoices", { params })
       .then(({ data }) => { if (!c) { setItems(data.items); setPages(data.pages); } })
       .catch((e) => { if (!c) setError(e); });
    return () => { c = true; };
  }, [page, status, q, reloadKey]);

  return (
    <div>
      <div className="flex items-end justify-between mb-4 gap-3 flex-wrap">
        <div className="flex items-end gap-3 flex-wrap">
          <div>
            <Label className="label-overline">Status</Label>
            <select data-testid="saas-inv-status"
                    value={status} onChange={(e) => setStatus(e.target.value)}
                    className="mt-1 h-9 border border-slate-300 rounded px-2 text-xs font-mono">
              <option value="">All</option>
              {["draft", "issued", "part_paid", "paid", "overdue", "void"].map((s) =>
                <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div>
            <Label className="label-overline">Search</Label>
            <Input data-testid="saas-inv-q" value={q}
                   onChange={(e) => setQ(e.target.value)}
                   placeholder="Invoice # or tenant"
                   className="mt-1 h-9 rounded text-xs font-mono" />
          </div>
        </div>
        <Link to="/platform/saas/invoices/new">
          <Button data-testid="saas-inv-new"
                  className="rounded bg-blue-700 hover:bg-blue-800 h-9">
            + New invoice
          </Button>
        </Link>
      </div>

      <div className="bg-white border border-slate-200 rounded overflow-hidden">
        {error ? (
          <div className="p-4">
            <WidgetError error={error} onRetry={() => setReloadKey((k) => k + 1)}
                         title="Could not load invoices" testId="saas-inv-error" />
          </div>
        ) : (
        <div className="overflow-x-auto">
          <table className="tbl w-full text-sm min-w-[720px]">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr className="label-overline text-slate-600">
                <th className="text-left px-4 py-3">Invoice #</th>
                <th className="text-left px-4 py-3">Tenant</th>
                <th className="text-left px-4 py-3">Period</th>
                <th className="text-left px-4 py-3">Total</th>
                <th className="text-left px-4 py-3">Paid</th>
                <th className="text-left px-4 py-3">Status</th>
                <th className="text-left px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 && (
                <tr><td colSpan={7} className="px-4 py-6 text-center text-slate-500">No invoices match</td></tr>
              )}
              {items.map((i) => (
                <tr key={i.id} className="border-b border-slate-100"
                    data-testid={`saas-inv-row-${i.id}`}>
                  <td className="px-4 py-2 font-mono">{i.invoice_no || <em className="text-slate-400">draft</em>}</td>
                  <td className="px-4 py-2">{i.tenant_snapshot?.business_name}</td>
                  <td className="px-4 py-2 font-mono text-xs">{i.period_start} → {i.period_end}</td>
                  <td className="px-4 py-2 font-mono">{fmtMoney(i.total_cents)}</td>
                  <td className="px-4 py-2 font-mono">{fmtMoney(i.paid_cents || 0)}</td>
                  <td className="px-4 py-2"><StatusBadge status={i.status} /></td>
                  <td className="px-4 py-2">
                    <Link to={`/platform/saas/invoices/${i.id}`}
                          className="text-blue-700 hover:underline text-xs font-semibold flex items-center gap-1">
                      Open <ChevronRight size={12} />
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        )}
        <PaginationBar page={page} pages={pages} onChange={setPage} />
      </div>
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    draft: "bg-slate-100 text-slate-700 border-slate-200",
    issued: "bg-blue-100 text-blue-800 border-blue-200",
    part_paid: "bg-amber-100 text-amber-800 border-amber-200",
    paid: "bg-green-100 text-green-800 border-green-200",
    overdue: "bg-red-100 text-red-800 border-red-200",
    void: "bg-slate-200 text-slate-500 border-slate-300 line-through",
    pending: "bg-amber-100 text-amber-800 border-amber-200",
    approved: "bg-green-100 text-green-800 border-green-200",
    rejected: "bg-red-100 text-red-800 border-red-200",
  };
  return (
    <span className={`inline-block px-2 py-0.5 text-[10px] font-mono font-bold uppercase border rounded ${map[status] || "bg-slate-100 text-slate-700"}`}>
      {status}
    </span>
  );
}

function SaasInvoiceCreate() {
  const nav = useNavigate();
  const [q, setQ] = useState("");
  const [matches, setMatches] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState({
    period_start: "", period_end: "", due_days: 7, notes: "",
  });
  const [submitting, setSubmitting] = useState(false);

  // Debounced server-side search of tenants — never loads full list.
  useEffect(() => {
    if (!q || q.length < 2 || selected) { setMatches([]); return; }
    const t = setTimeout(() => {
      setSearching(true);
      api.get("/platform/tenants", { params: { q, limit: 10 } })
         .then(({ data }) => setMatches(data.items || []))
         .catch(() => setMatches([]))
         .finally(() => setSearching(false));
    }, 300);
    return () => clearTimeout(t);
  }, [q, selected]);

  const pick = (t) => { setSelected(t); setQ(""); setMatches([]); };
  const clear = () => { setSelected(null); setQ(""); setMatches([]); };

  const submit = async (e) => {
    e.preventDefault();
    if (!selected) return toast.error("Select a tenant first");
    setSubmitting(true);
    try {
      const { data } = await api.post("/platform/saas/invoices",
        { ...form, tenant_id: selected.id });
      toast.success("Invoice draft created");
      nav(`/platform/saas/invoices/${data.id}`);
    } catch (err) { toast.error(formatError(err, "Create failed")); }
    finally { setSubmitting(false); }
  };

  return (
    <form onSubmit={submit} className="max-w-lg bg-white border border-slate-200 rounded p-5 space-y-4">
      <div>
        <Label className="label-overline">Tenant</Label>
        {selected ? (
          <div className="mt-1 flex items-center gap-2 border border-blue-300 bg-blue-50 rounded px-3 py-2"
               data-testid="saas-inv-new-tenant-selected">
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-sm truncate">{selected.business_name}</div>
              <div className="font-mono text-[11px] text-slate-500">{selected.plan}</div>
            </div>
            <button type="button" onClick={clear}
                    data-testid="saas-inv-new-tenant-clear"
                    className="text-xs text-blue-700 hover:underline font-semibold">Change</button>
          </div>
        ) : (
          <div className="relative mt-1">
            <Input value={q}
                   data-testid="saas-inv-new-tenant-search"
                   onChange={(e) => setQ(e.target.value)}
                   placeholder="Type to search tenants…"
                   className="rounded h-10 font-mono" />
            {q.length >= 2 && (
              <div className="mt-1 border border-slate-200 rounded bg-white shadow-sm max-h-64 overflow-auto"
                   data-testid="saas-inv-new-tenant-results">
                {searching && (
                  <div className="px-3 py-2 text-xs text-slate-500">Searching…</div>
                )}
                {!searching && matches.length === 0 && (
                  <div className="px-3 py-2 text-xs text-slate-500">No matches</div>
                )}
                {matches.map((t) => (
                  <button type="button" key={t.id}
                          onClick={() => pick(t)}
                          data-testid={`saas-inv-new-tenant-opt-${t.id}`}
                          className="w-full text-left px-3 py-2 hover:bg-slate-50 border-b border-slate-100 last:border-0">
                    <div className="font-semibold text-sm truncate">{t.business_name}</div>
                    <div className="font-mono text-[11px] text-slate-500">
                      {t.plan} · {t.slug}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="label-overline">Period start</Label>
          <Input required type="date" data-testid="saas-inv-new-start"
                 value={form.period_start}
                 onChange={(e) => setForm({ ...form, period_start: e.target.value })}
                 className="mt-1 rounded h-10 font-mono" />
        </div>
        <div>
          <Label className="label-overline">Period end</Label>
          <Input required type="date" data-testid="saas-inv-new-end"
                 value={form.period_end}
                 onChange={(e) => setForm({ ...form, period_end: e.target.value })}
                 className="mt-1 rounded h-10 font-mono" />
        </div>
      </div>
      <div>
        <Label className="label-overline">Due (days)</Label>
        <Input type="number" min="0" max="90"
               data-testid="saas-inv-new-due"
               value={form.due_days}
               onChange={(e) => setForm({ ...form, due_days: parseInt(e.target.value || "7", 10) })}
               className="mt-1 rounded h-10 font-mono w-32" />
      </div>
      <div>
        <Label className="label-overline">Notes (optional)</Label>
        <Textarea value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  className="rounded mt-1" rows={3} />
      </div>
      <div className="pt-2">
        <Button data-testid="saas-inv-new-submit" type="submit"
                disabled={submitting || !selected}
                className="rounded bg-blue-700 hover:bg-blue-800">
          {submitting ? "Creating…" : "Create draft"}
        </Button>
      </div>
    </form>
  );
}

function SaasInvoiceDetail() {
  const { id } = useParams();
  const [inv, setInv] = useState(null);
  const [error, setError] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);

  const reload = useCallback(() => setReloadKey((k) => k + 1), []);

  useEffect(() => {
    let c = false;
    setError(null);
    api.get(`/platform/saas/invoices/${id}`)
       .then(({ data }) => { if (!c) setInv(data); })
       .catch((e) => { if (!c) setError(e); });
    return () => { c = true; };
  }, [id, reloadKey]);

  const issue = async () => {
    if (!window.confirm("Issue this invoice? An invoice number and PDF will be generated. This cannot be undone.")) return;
    try {
      await api.post(`/platform/saas/invoices/${id}/issue`);
      toast.success("Invoice issued");
      reload();
    } catch (e) { toast.error(formatError(e, "Issue failed")); }
  };
  const doVoid = async () => {
    const reason = window.prompt("Reason for voiding?");
    if (!reason || reason.length < 3) return;
    try {
      await api.post(`/platform/saas/invoices/${id}/void`, { reason });
      toast.success("Invoice voided");
      reload();
    } catch (e) { toast.error(formatError(e, "Void failed")); }
  };
  const openPdf = async () => {
    try {
      const res = await fetch(`${API_BASE}/platform/saas/invoices/${id}/pdf`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      if (!res.ok) throw new Error(`Failed (${res.status})`);
      window.open(URL.createObjectURL(await res.blob()), "_blank");
    } catch (e) { toast.error(formatError(e, "PDF failed")); }
  };

  if (error) return <WidgetError error={error} title="Could not load invoice" testId="saas-inv-detail-error" />;
  if (!inv) return <div className="label-overline text-slate-500">Loading…</div>;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
      <div className="lg:col-span-2 bg-white border border-slate-200 rounded p-6">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <div className="label-overline">SaaS invoice</div>
            <div className="text-2xl font-black font-mono mt-1">{inv.invoice_no || "DRAFT"}</div>
            <div className="text-sm text-slate-600 mt-1">
              {inv.tenant_snapshot?.business_name} · {inv.period_start} → {inv.period_end}
            </div>
          </div>
          <StatusBadge status={inv.status} />
        </div>
        <table className="w-full mt-6 text-sm">
          <thead className="border-b border-slate-200 label-overline text-slate-600">
            <tr>
              <th className="text-left py-2">Description</th>
              <th className="text-right py-2">Qty</th>
              <th className="text-right py-2">Unit</th>
              <th className="text-right py-2">Total</th>
            </tr>
          </thead>
          <tbody>
            {inv.lines.map((l, i) => (
              <tr key={i} className="border-b border-slate-100">
                <td className="py-2">{l.description}</td>
                <td className="py-2 text-right font-mono">{l.quantity.toFixed(2)}</td>
                <td className="py-2 text-right font-mono">{fmtMoney(l.unit_price_cents)}</td>
                <td className="py-2 text-right font-mono">{fmtMoney(l.line_total_cents)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr><td colSpan={3} className="pt-3 text-right label-overline">Subtotal</td>
                <td className="pt-3 text-right font-mono">{fmtMoney(inv.subtotal_cents)}</td></tr>
            {inv.discount_cents > 0 && (
              <tr><td colSpan={3} className="text-right label-overline">Discount</td>
                  <td className="text-right font-mono">-{fmtMoney(inv.discount_cents)}</td></tr>
            )}
            <tr className="font-black text-lg">
              <td colSpan={3} className="pt-2 text-right">Total due</td>
              <td className="pt-2 text-right font-mono">{fmtMoney(inv.total_cents)}</td>
            </tr>
            {inv.paid_cents > 0 && (
              <tr className="text-green-700">
                <td colSpan={3} className="text-right label-overline">Paid</td>
                <td className="text-right font-mono">{fmtMoney(inv.paid_cents)}</td>
              </tr>
            )}
          </tfoot>
        </table>
        {inv.notes && (
          <div className="mt-6 p-3 bg-slate-50 rounded text-xs text-slate-700 long-value">
            {inv.notes}
          </div>
        )}
      </div>
      <div className="space-y-3">
        <div className="bg-white border border-slate-200 rounded p-4 space-y-2">
          {inv.status === "draft" && (
            <Button data-testid="saas-inv-issue-btn" onClick={issue}
                    className="rounded bg-blue-700 hover:bg-blue-800 w-full">Issue invoice</Button>
          )}
          {inv.status !== "draft" && inv.status !== "paid" && inv.status !== "void" && (
            <Button data-testid="saas-inv-void-btn" onClick={doVoid} variant="outline"
                    className="rounded w-full">Void invoice…</Button>
          )}
          {inv.pdf_file_id && (
            <Button data-testid="saas-inv-pdf-btn" onClick={openPdf} variant="outline"
                    className="rounded w-full">View PDF</Button>
          )}
        </div>
        {inv.void_reason && (
          <div className="bg-red-50 border border-red-200 rounded p-3 text-xs">
            <div className="label-overline text-red-700">Voided</div>
            <div className="mt-1 long-value">{inv.void_reason}</div>
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------- Payments queue + list ----------------

function SaasPaymentsQueue() {
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [error, setError] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let c = false;
    setError(null);
    api.get("/platform/saas/payments/queue", { params: { page, limit: 20 } })
       .then(({ data }) => { if (!c) { setItems(data.items); setPages(data.pages); } })
       .catch((e) => { if (!c) setError(e); });
    return () => { c = true; };
  }, [page, reloadKey]);

  const reload = () => setReloadKey((k) => k + 1);

  const viewProof = async (p) => {
    if (!p.proof_file_id) return;
    try {
      const res = await fetch(`${API_BASE}/files/${p.proof_file_id}`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      if (!res.ok) throw new Error(`Failed (${res.status})`);
      window.open(URL.createObjectURL(await res.blob()), "_blank");
    } catch (e) {
      toast.error(formatError(e, "Could not open proof of payment"));
    }
  };

  const approve = async (p) => {
    if (!window.confirm(`Approve ${fmtMoney(p.amount_cents)} against ${p.invoice_no}?`)) return;
    try {
      await api.post(`/platform/saas/payments/${p.id}/approve`, {});
      toast.success("Payment approved");
      reload();
    } catch (e) { toast.error(formatError(e, "Approve failed")); }
  };
  const reject = async (p) => {
    const reason = window.prompt("Rejection reason?");
    if (!reason || reason.length < 3) return;
    try {
      await api.post(`/platform/saas/payments/${p.id}/reject`, { reason });
      toast.success("Payment rejected");
      reload();
    } catch (e) { toast.error(formatError(e, "Reject failed")); }
  };

  if (error) return <WidgetError error={error} title="Could not load queue" testId="saas-queue-error" />;
  return (
    <div className="bg-white border border-slate-200 rounded overflow-hidden">
      <div className="overflow-x-auto">
        <table className="tbl w-full text-sm min-w-[720px]">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr className="label-overline text-slate-600">
              <th className="text-left px-4 py-3">Payment #</th>
              <th className="text-left px-4 py-3">Tenant</th>
              <th className="text-left px-4 py-3">Invoice</th>
              <th className="text-left px-4 py-3">Amount</th>
              <th className="text-left px-4 py-3">Proof</th>
              <th className="text-left px-4 py-3">Submitted</th>
              <th className="text-left px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && (
              <tr><td colSpan={7} className="px-4 py-6 text-center text-slate-500">Queue is empty</td></tr>
            )}
            {items.map((p) => (
              <tr key={p.id} className="border-b border-slate-100"
                  data-testid={`saas-queue-row-${p.id}`}>
                <td className="px-4 py-2 font-mono text-xs">{p.payment_ref}</td>
                <td className="px-4 py-2 text-xs long-value">
                  {p.tenant_business_name || p.tenant_id?.slice(0, 8)}
                </td>
                <td className="px-4 py-2 font-mono">{p.invoice_no || "—"}</td>
                <td className="px-4 py-2 font-mono">{fmtMoney(p.amount_cents)}</td>
                <td className="px-4 py-2 text-xs">
                  {p.proof_file_id ? (
                    <button type="button" onClick={() => viewProof(p)}
                            data-testid={`saas-view-proof-${p.id}`}
                            className="text-blue-700 hover:underline font-semibold">
                      View
                    </button>
                  ) : "—"}
                </td>
                <td className="px-4 py-2 font-mono text-xs">{fmtDate(p.created_at)}</td>
                <td className="px-4 py-2 whitespace-nowrap">
                  <button data-testid={`saas-approve-${p.id}`}
                          onClick={() => approve(p)}
                          className="inline-flex items-center gap-1 text-xs text-green-700 hover:underline font-semibold mr-3">
                    <CheckCircle2 size={12} /> Approve
                  </button>
                  <button data-testid={`saas-reject-${p.id}`}
                          onClick={() => reject(p)}
                          className="inline-flex items-center gap-1 text-xs text-red-700 hover:underline font-semibold">
                    <XCircle size={12} /> Reject
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <PaginationBar page={page} pages={pages} onChange={setPage} />
    </div>
  );
}

function SaasPayments() {
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [status, setStatus] = useState("");
  const [method, setMethod] = useState("");
  const [error, setError] = useState(null);

  useEffect(() => { setPage(1); }, [status, method]);

  useEffect(() => {
    let c = false;
    setError(null);
    const params = { page, limit: 20 };
    if (status) params.status = status;
    if (method) params.method = method;
    api.get("/platform/saas/payments", { params })
       .then(({ data }) => { if (!c) { setItems(data.items); setPages(data.pages); } })
       .catch((e) => { if (!c) setError(e); });
    return () => { c = true; };
  }, [page, status, method]);

  if (error) return <WidgetError error={error} title="Could not load payments" testId="saas-payments-error" />;
  return (
    <>
      <div className="flex items-end gap-3 mb-3">
        <div>
          <Label className="label-overline">Status</Label>
          <select value={status} onChange={(e) => setStatus(e.target.value)}
                  className="mt-1 h-9 border border-slate-300 rounded px-2 text-xs font-mono">
            <option value="">All</option>
            {["pending", "approved", "rejected", "failed", "succeeded"].map((s) =>
              <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        <div>
          <Label className="label-overline">Method</Label>
          <select value={method} onChange={(e) => setMethod(e.target.value)}
                  className="mt-1 h-9 border border-slate-300 rounded px-2 text-xs font-mono">
            <option value="">All</option>
            {["manual_eft", "payfast", "ozow"].map((m) =>
              <option key={m} value={m}>{m}</option>)}
          </select>
        </div>
      </div>
      <div className="bg-white border border-slate-200 rounded overflow-hidden">
        <div className="overflow-x-auto">
          <table className="tbl w-full text-sm min-w-[720px]">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr className="label-overline text-slate-600">
                <th className="text-left px-4 py-3">Ref</th>
                <th className="text-left px-4 py-3">Method</th>
                <th className="text-left px-4 py-3">Invoice</th>
                <th className="text-left px-4 py-3">Amount</th>
                <th className="text-left px-4 py-3">Status</th>
                <th className="text-left px-4 py-3">When</th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 && (
                <tr><td colSpan={6} className="px-4 py-6 text-center text-slate-500">No payments match</td></tr>
              )}
              {items.map((p) => (
                <tr key={p.id} className="border-b border-slate-100">
                  <td className="px-4 py-2 font-mono text-xs">{p.payment_ref}</td>
                  <td className="px-4 py-2 font-mono text-xs">{p.method}</td>
                  <td className="px-4 py-2 font-mono">{p.invoice_no || "—"}</td>
                  <td className="px-4 py-2 font-mono">{fmtMoney(p.amount_cents)}</td>
                  <td className="px-4 py-2"><StatusBadge status={p.status} /></td>
                  <td className="px-4 py-2 font-mono text-xs">{fmtDate(p.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <PaginationBar page={page} pages={pages} onChange={setPage} />
      </div>
    </>
  );
}
